import { CommonModule } from "@angular/common";
import { Component, OnInit, Inject, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogModule, MatDialogRef, MatDialogTitle } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatTableModule } from "@angular/material/table";
import { MatRadioModule } from "@angular/material/radio";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { provideNativeDateAdapter } from "@angular/material/core";
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatCheckboxModule } from "@angular/material/checkbox";
import { Subject, takeUntil } from "rxjs";
import { SearchCustomersDialogComponent } from "../../common/search-customers-dialog/search-customers-dialog.component";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { ProductPromotionRequestDialogData } from "../../../../models/common/ProductPromotionRequestDialogData";
import { CommonDataService } from "../../../../services/common-data.service";
import { CollaborationHistoryEnum, CustomerSearchType, EstimateStatus, ForcePublishDetails, Material, MaterialLevelwiseData, PpaMaterials, PpaStatus, ProductPromotionRequest, ProductPromotionStatusRequest, ProductPromotionStatusResponse, SubSystem } from "../../../../models/dtos";
import { CONSTANTS } from "../../../../Constants/Constants";
import { Business } from "../../../../models/dtos/Enums/Business";
import { IndexDBService } from "../../../../services/common/indexDB.service";
import { ProductPromotionService } from "../../../../services/api/product-promotion.service";
import { NotificationMessageService } from "../../../../services/common/notification-message.service";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { MultilingualCurrencyPipe } from "../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import { UserProfileService } from "../../../../services/common/user-profile.service";
import { CommonMetaDataService } from "../../../../services/common-meta-data.service";
import { CloseOnEscapeDirective } from "../../../../directives/close-on-escape.directive";
import { GocCollaborationRestrictionHandlerService } from "../../../../services/common/goc-collaboration-restriction-handler.service";
import { NumericMaskDirective } from "../../../../shared/numeric/numeric-mask.directive";
import { NumericFieldConfig } from "../../../../shared/numeric/numeric.types";
import { CommonFunctionsService } from "../../../../services/common-functions.service";

@Component({
  selector: 'app-sol-product-promotion-request-dialog',
  templateUrl: './sol-product-promotion-request.component.html',
  styleUrl: './sol-product-promotion-request.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTableModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    MatCheckboxModule,
    MatDatepickerModule,
    TranslatePipe,
    MultilingualCurrencyPipe,
    CloseOnEscapeDirective,
    NumericMaskDirective
  ],
  providers: [
    ...provideNgxMask(),
    provideNativeDateAdapter()
  ]
})
export class SolProductPromotionRequestDialog implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private submittedRequest: ProductPromotionRequest | null = null;

    productPromotionRequestForm: FormGroup<{
      descriptionOfProject: FormControl<string>;
      preferenceExpected: FormControl<string>;
      contactNumber: FormControl<string>;
      requiredGM: FormControl<number>;
      proposalDate: FormControl<Date>;
      requiredDiscount: FormControl<number>;
      jobStartDate: FormControl<string>;
      affectedMaterials: FormControl<string>;
      authorization: FormControl<string>;
      business: FormControl<string>;
      requestor: FormControl<string>;
      salesOffice: FormControl<string>;
      customerName: FormControl<string>;
      endUser: FormControl<string>;
      cpsMaterialEstimatedValue: FormControl<number>;
      estimatedSellPrice: FormControl<number>;
      gm_percent: FormControl<number>;
      overrideRequirements: FormControl<boolean>;
    }> = new FormGroup({
      descriptionOfProject: new FormControl(''),
      preferenceExpected: new FormControl(''),
      contactNumber: new FormControl(''),
      requiredGM: new FormControl(0),
      proposalDate: new FormControl(new Date()),
      requiredDiscount: new FormControl(0),
      jobStartDate: new FormControl(''),
      affectedMaterials: new FormControl(''),
      authorization: new FormControl(''),
      business: new FormControl(''),
      requestor: new FormControl(''),
      salesOffice: new FormControl(''),
      customerName: new FormControl(''),
      endUser: new FormControl(''),
      cpsMaterialEstimatedValue: new FormControl(0),
      estimatedSellPrice: new FormControl(0),
      gm_percent: new FormControl(0),
      overrideRequirements: new FormControl(false),
    });

    allMaterialData: MaterialLevelwiseData = new MaterialLevelwiseData();
    ppaMaterials: PpaMaterials[] = [];
    disableSearchBtn: boolean = false;

    readonly requiredGMConfig: NumericFieldConfig = {
      kind: 'percentage',
      scale: CONSTANTS.TWO,
      min: CONSTANTS.ZERO,
      max: CONSTANTS.HUNDRED,
      step: CONSTANTS.ZERO_POINT_ONE,
      useGrouping: false,
      revertToLastValidValue: true,
    }

    readonly requiredDiscountConfig: NumericFieldConfig = {
      kind: 'percentage',
      scale: CONSTANTS.ZERO,
      min: CONSTANTS.ZERO,
      max: CONSTANTS.HUNDRED,
      step: CONSTANTS.ONE,
      useGrouping: false,
      revertToLastValidValue: true,
    }

    constructor(
      private readonly dialog: MatDialog,
      @Inject(MAT_DIALOG_DATA) private readonly data: ProductPromotionRequestDialogData,
      private readonly commonDataService: CommonDataService,
      private readonly indexDBService: IndexDBService,
      private readonly productPromotionService: ProductPromotionService,
      private readonly notificationService: NotificationMessageService,
      private readonly userProfileService: UserProfileService,
      private readonly commonMetaDataService: CommonMetaDataService,
      private readonly dialogRef: MatDialogRef<SolProductPromotionRequestDialog>,
      private readonly translate: TranslateService,
      private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
      private readonly commonFunctionsService: CommonFunctionsService
    ) {}
    
    ngOnInit(): void {
      this.prefillFormData();
      this.loadAllMaterialData();
      if(this.data && this.data.isRequested){
        this.disableFieldsAfterRequest();
        this.populateStatusDetails();
      }
    }

    private populateStatusDetails(): void {
      const statusDetails = this.data?.statusDetails;
      if (!statusDetails) {
        return;
      }

      this.productPromotionRequestForm.controls.authorization.setValue(this.getStatus(statusDetails.ppaStatus));
      this.productPromotionRequestForm.patchValue({
        descriptionOfProject: statusDetails.description,
        preferenceExpected: statusDetails.preferenceExpected,
        contactNumber: statusDetails.contactNumber,
        requiredGM: statusDetails.requiredGM,
        proposalDate: new Date(statusDetails.proposalDate),
        requiredDiscount: statusDetails.requiredDiscountPercent,
        jobStartDate: statusDetails.jobStartDate,
        requestor: statusDetails.requestor,
        customerName: statusDetails.customer,
        endUser: statusDetails.endUser,
        cpsMaterialEstimatedValue: statusDetails.cpsMaterialValue,
        estimatedSellPrice: statusDetails.estimatedSellPrice,
        gm_percent: statusDetails.gmPercentage,
        overrideRequirements: statusDetails.overrideRequirements
      });
    }
    

   loadAllMaterialData() {
      this.indexDBService.getAllMaterialData().then((data) => {
        this.allMaterialData = data;
      });
    }

    prefillFormData() {
      const customerInfo = this.commonDataService.getOtherMetaData()?.customerInformation;
      const estimateTabData = this.commonDataService.getEstimateMetaData()?.estimateTab;
      const grossMargin = this.commonDataService.getOtherMetaData()?.grossMargin;
      const estimateData = this.commonDataService.getEstimateData();
      const simensMaterialCost = estimateTabData?.siemensMaterialCost || 0;
      const cpsMaterialCost = this.data?.affectedMaterialText === CONSTANTS.SIEMENS_MATERIAL ? simensMaterialCost : 0;

      this.productPromotionRequestForm.patchValue({
        affectedMaterials: this.data?.affectedMaterialText,
        jobStartDate: new Date(estimateData?.startDate).toDateString() || '',
        requestor: this.userProfileService.userInfo?.gid || '',
        business: this.data?.business ? this.getBusinessText(this.data.business) : CONSTANTS.EMPTY_STRING,
        salesOffice: estimateData?.salesOfficeId ? this.getSalesOfficeText(estimateData.salesOfficeId) : CONSTANTS.EMPTY_STRING,
        customerName: customerInfo?.customerName || '',
        endUser: customerInfo?.endUserName || '',
        gm_percent: grossMargin?.soldASGMPercent || 0,
        estimatedSellPrice: estimateTabData?.soldASSellPrice || 0,
        cpsMaterialEstimatedValue: cpsMaterialCost,
        proposalDate: new Date(),
        requiredDiscount: this.data?.isRequested ? 0 : this.data.allowedDiscountPercentage || 0,
        authorization: this.getStatus(this.data?.status)
      });
    }

    getSalesOfficeText(salesOfficeId: number): string {
      const salesOfficesDetails = this.commonMetaDataService.getMetaData()?.salesOfficesDetails;
      return salesOfficesDetails?.find(office => office.salesOfficeId === salesOfficeId)?.salesOfficeName || '';
    }

    getFormStringValue(controlName: string): string {
      return (this.productPromotionRequestForm.get(controlName)?.value as string) || '';
    }

    getFormNumberValue(controlName: string): number {
      return Number(this.productPromotionRequestForm.get(controlName)?.value) || 0;
    }

    getStatus(status: PpaStatus): string {
      if (!status) {
        return '';
      }
      if (status === PpaStatus.Pending) {
        return 'Pending';
      } else {
        return '';
      }
    }

  disableSubmitBtn(): boolean {
    return this.data && this.data.isRequested;
  }

  disableFieldsAfterRequest(){
    this.disableSearchBtn = true;
    this.productPromotionRequestForm.controls.descriptionOfProject.disable();
    this.productPromotionRequestForm.controls.preferenceExpected.disable();
    this.productPromotionRequestForm.controls.requiredGM.disable();
    this.productPromotionRequestForm.controls.requiredDiscount.disable();
    this.productPromotionRequestForm.controls.proposalDate.disable();
    this.productPromotionRequestForm.controls.contactNumber.disable();
    this.productPromotionRequestForm.controls.customerName.disable();
    this.productPromotionRequestForm.controls.endUser.disable();
  }

  onRequestBtnClick(): void {
    this.generatePPAMaterialCollection();
    const request = this.createRequestObject();
    this.data.isRequested = true;
    this.productPromotionService.submitProductPromotionRequest(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.submittedRequest = request;
          this.notificationService.showNotificationMessageDialog(
            CONSTANTS.TYPE_SUCCESS,
            'SUCCESS_TITLE',
            'PRODUCT_PROMOTION_REQUEST_SUCCESS_MSG',
            CONSTANTS.DEFAULT_DIALOG_WIDTH);
          this.productPromotionRequestForm.controls.authorization.setValue('Pending');
          this.disableFieldsAfterRequest();
        if (request.isCollaborativeEstimate && response.collaborationHubData) {
                const collaborationHubData = response.collaborationHubData;
                this.commonFunctionsService.updateCollaborationHubData(collaborationHubData);
          }
        },
        error: async (error) => {
          if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
            return;
          }
          console.error('Error submitting product promotion request:', error);
          this.data.isRequested = false;
        }
      });
  }

  getBusinessText(businessValue: string): string {
    switch (businessValue) {
      case CONSTANTS.BUSINESS_AUTOMATION:
        return 'Automation';
      case CONSTANTS.BUSINESS_FIRE:
        return 'Fire';
      case CONSTANTS.BUSINESS_SECURITY:
        return 'Security';
      case CONSTANTS.BUSINESS_ELECTRICAL:
        return 'Electrical';
      case CONSTANTS.BUSINESS_MECHANICAL:
        return 'Mechanical';
      default:
        return '';
      }
    }

  getBusinessValue(businessText: string): number {
    switch (businessText) {
      case CONSTANTS.BUSINESS_AUTOMATION:
        return Business.Automation;
      case CONSTANTS.BUSINESS_FIRE:
        return Business.Fire;
      case CONSTANTS.BUSINESS_SECURITY:
        return Business.Security;
      case CONSTANTS.BUSINESS_ELECTRICAL:
        return Business.Electrical;
      case CONSTANTS.BUSINESS_MECHANICAL:
        return Business.Mechanical;
      default:
        return 0;
    }
  }

  private getAffectedMaterial(affectedMaterialText: string): string {
    switch (affectedMaterialText) {
      case CONSTANTS.SIEMENS_MATERIAL:
        return 'S';
      case CONSTANTS.VESDA_MATERIAL:
        return 'V';
      case CONSTANTS.MASS_NOTIFICATION_DEVICES:
        return 'M';
      case CONSTANTS.SIPASS_MATERIAL:
        return 'P';
      default:
        return '';
    } 
  }
     createRequestObject(): ProductPromotionRequest {
      const formValue = this.productPromotionRequestForm.value;
      const estimateId = this.commonDataService.getEstimateId() || '';
      const estimateName = this.commonDataService.getSQWebProject()?.itemName || '';
      const estimateData = this.commonDataService.getEstimateData();
      const isCollaborativeEstimate = this.commonDataService.isCollaborationEstimate();
     
      const request: ProductPromotionRequest = {
        description: formValue.descriptionOfProject || '',
        preferenceExpected: formValue.preferenceExpected || '',
        contactNumber: formValue.contactNumber || '',
        requiredGM: formValue.requiredGM || 0,
        proposalDate: formValue.proposalDate ? formValue.proposalDate: new Date(),
        requiredDiscountPercent: formValue.requiredDiscount || 0,
        jobStartDate: formValue.jobStartDate || '',
        affectedMaterial: this.getAffectedMaterial(formValue.affectedMaterials) || '',
        business: this.getBusinessValue(formValue.business),
        requestor: formValue.requestor || '',
        salesOfficeId: estimateData?.salesOfficeId ? Number(estimateData.salesOfficeId) : 0,
        customer: formValue.customerName || '',
        endUser: formValue.endUser || '',
        cpsMaterialValue: formValue.cpsMaterialEstimatedValue || 0,
        estimatedSellPrice: formValue.estimatedSellPrice || 0,
        gmPercentage: formValue.gm_percent || 0,
        overrideRequirements: formValue.overrideRequirements || false,
        discountDollars: 0, // TO-DO: Calculate based on discount percent and sell price
        estimateName: estimateName,
        estimateId: estimateId,
        cultureID: this.commonDataService.getEstimateCultureId(),
        ppaMaterialCollection:  this.ppaMaterials,
        isCollaborativeEstimate: isCollaborativeEstimate,
        userId: this.userProfileService.userInfo?.gid || '',
        forcePublishDetails: this.getForcePublishDetails(isCollaborativeEstimate)
      };
      
      return request;
    }

    private getForcePublishDetails(isCollaborativeEstimate: boolean): ForcePublishDetails | null {
      if (!isCollaborativeEstimate){
        return null;
      }
      const estimateData = this.commonDataService.getEstimateData();
      if (estimateData?.estimateStatusId === EstimateStatus.Submitted) {
        return null;
      }
      const forcePublishDetails: ForcePublishDetails = {
        forcePublishAdditionalInfo: CONSTANTS.ESTIMATE_FORCE_PUBLISH_PRODUCT_PROMOTION,
        operation: CollaborationHistoryEnum.ProductPromotion,
        modificationNotes: this.translate.instant('PRODUCT_PROMOTION_SYSTEM_PUBLISHED'),
        estimateCheckoutAdditionalInfo: CONSTANTS.ESTIMATE_ADD_ON_PRODUCT_PROMOTION,
        isEstimateBeingCheckedOutAfterPublish: true
      };

      return forcePublishDetails;
    }

    generatePPAMaterialCollection(): void {
      const  affectedMaterials  = this.productPromotionRequestForm.get('affectedMaterials')?.value;
      if(affectedMaterials && affectedMaterials !== CONSTANTS.SIEMENS_MATERIAL) {
        this.ppaMaterials = [];
        return;
      }
      if (affectedMaterials && this.allMaterialData?.materialsCollection) {
        // Process each material in the collection
        Object.keys(this.allMaterialData.materialsCollection).forEach(materialUniqueId => {
          this.iterateMaterialData(materialUniqueId);
        });
      }
    }

  iterateMaterialData(materialUniqueId: string) {
    if (this.allMaterialData && this.allMaterialData.materialsCollection) {
      const systemChildrenCollection = this.allMaterialData.materialsCollection[materialUniqueId].systemChildrenCollection;
      if (systemChildrenCollection && systemChildrenCollection.length) {
        for (const child of systemChildrenCollection) {
          if (child.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
            (child as SubSystem).children.forEach((childMaterialItem) => {
              this.createPPACollection(childMaterialItem as Material);
            });
          } else {
            this.createPPACollection(child as Material);
          }
        }
      }
    }
  }

  createPPACollection(material: Material): void {
    if (!CONSTANTS.SIEMENS_MATERIAL_TYPES.has(material.materialType)) {
      return;
    }
    const ppaMaterial: PpaMaterials = {
      referenceNumber: material.referenceNumber || '',
      ssn: material.ssn,
      modelNumber: material.modelNumber || '',
      description: material.description || '',
      ppmCost: material.ppmCost || 0,
      cost: material.cost || 0,
      quantity: material.quantity || 0
    };
    
    this.ppaMaterials.push(ppaMaterial);
  }

    openCustomerDialog(): void {
      const dialogref = this.dialog.open(SearchCustomersDialogComponent, {
        width: '800px',
        disableClose: true,
        data: CustomerSearchType.AccountName
      });

      dialogref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response && response.customerInfo) {
          this.productPromotionRequestForm.patchValue({ customerName: response.customerInfo.customerName });
        }
      });
    }

    closeDialog(): void {
      this.dialogRef.close({
        isRequested: this.data.isRequested,
        submittedRequest: this.submittedRequest
      });
    }

    ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
      this.allMaterialData = null;
      this.ppaMaterials = [];
    }

}

